import { Component, computed, inject, input, signal } from '@angular/core';
import { AssetService } from '../services/asset-service/asset.service';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { tablerLock, tablerDownload } from '@ng-icons/tabler-icons';
import { PrimaryBtnDirective } from './primary-button';
import { Loader } from './loader';
import { RequestAccess } from './request-access/request-access';

@Component({
  selector: 'asset-download-button',
  template: `
    @if (hasDownload()) {
      @if (hasAccess()) {
        @if (isIconButton()) {
          <button
            type="button"
            class="icon-button"
            (click)="ctaAction($event)"
            [disabled]="isDownloading()"
            title="Download"
          >
            @if (isDownloading()) {
              <loader color="white" size="14px" />
            } @else {
              <ng-icon size="14px" name="tablerDownload" />
            }
          </button>
        } @else {
          <button
            appPrimaryBtn
            type="button"
            class="full-button"
            (click)="ctaAction($event)"
            [disabled]="isDownloading()"
          >
            Download
          </button>
        }
      } @else {
        <request-access
          [assetId]="assetId()"
          [assetName]="assetName()"
          [isIconButton]="isIconButton()"
          [ownerName]="ownerName()"
          [ownerEmail]="ownerEmail()"
        />
      }
    }
  `,
  styles: `
    .icon-button {
      width: 28px;
      height: 28px;
      border: none;
      background: none;
      cursor: pointer;
      border-radius: 6px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #007db7;
      transition:
        background 0.15s,
        color 0.15s;

      &:hover {
        background: rgba(0, 125, 183, 0.1);
      }

      &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
        pointer-events: none;
      }
    }

    .full-button {
      min-width: 120px;
    }
  `,
  imports: [NgIcon, PrimaryBtnDirective, Loader, RequestAccess],
  providers: [provideIcons({ tablerLock, tablerDownload })],
})
export class AssetDownloadButton {
  assetService = inject(AssetService);

  isIconButton = input(false);
  hasDownload = input.required<boolean>();
  hasAccess = input.required<boolean>();
  assetId = input.required<string>();
  assetName = input.required<string>();
  readonly ownerName = input.required<string | null>();
  readonly ownerEmail = input.required<string | null>();

  isDownloading = signal(false);

  readonly canDownload = computed(() => {
    return this.hasAccess() && this.hasDownload();
  });

  ctaAction = (e: Event): void => {
    e.stopPropagation();
    if (!this.hasDownload()) {
      return; // nothing to do if no download
    }
    if (!this.hasAccess()) {
      return; // shouldn't be possible as we should show request access button
    }

    this.isDownloading.set(true);
    // download
    this.assetService
      .getAssetDownloadDetails(this.canDownload, this.assetId, { source: 'ASSET_DETAIL' })
      .subscribe({
        next: (res) => {
          if (res === null) {
            // TODO: handle null case
            return;
          }
          const fileUrl = res.location_uri;
          const anchor = document.createElement('a');
          anchor.href = fileUrl;
          document.body.appendChild(anchor);
          anchor.click();

          // Cleanup
          document.body.removeChild(anchor);
          this.isDownloading.set(false);
        },
        error: (err) => {
          // TODO: show user feedback in ui?
          console.error('Download workflow failed:', err);
          this.isDownloading.set(false);
        },
      });
  };
}
